<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import lumeratecConstants from 'dashboard/constants/globals';

import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');

const canManuallyAssume = computed(() => {
  if (currentAccount.value?.role === 'administrator') return true;
  if (!currentAccount.value?.custom_role_id) return true;

  return (currentAccount.value?.permissions || []).includes(
    'conversation_assign_manual'
  );
});

const canManageUnassigned = computed(() => {
  if (currentAccount.value?.role === 'administrator') return true;
  if (!currentAccount.value?.custom_role_id) return true;

  const permissions = currentAccount.value?.permissions || [];
  return (
    permissions.includes('conversation_manage') ||
    permissions.includes('conversation_unassigned_manage')
  );
});
const isUnassignedQueueContext = computed(
  () => route.query.assignee_tab === 'unassigned'
);

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value?.id,
      assignee: agent,
    });
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
      assignmentSource: isUnassignedQueueContext.value
        ? 'unassigned_queue'
        : null,
    });
  },
});

const isUserTyping = computed(
  () => props.message !== '' && !props.isOnPrivateNote
);
const isUnassigned = computed(() => !assignedAgent.value);
const isEligibleUnassignedQueueConversation = computed(() => {
  const attributes = currentChat.value?.custom_attributes || {};
  return (
    currentChat.value?.status !== lumeratecConstants.STATUS_TYPE.CAMPAIGN &&
    !attributes.lumerai_event_state?.event_id &&
    !attributes.active_lumerai_flow_id &&
    !['active', 'handed_over'].includes(attributes.ai_autopilot_status)
  );
});
const isAssignedToOtherAgent = computed(
  () => assignedAgent.value?.id !== currentUser.value?.id
);

const isLumeraiHandoffAwaitingAssumption = computed(
  () =>
    !props.isOnPrivateNote &&
    currentChat.value?.custom_attributes?.ai_autopilot_status ===
      'handed_over' &&
    isUnassigned.value
);

const requiresAssumptionBeforeReply = computed(
  () => !props.isOnPrivateNote && isAssignedToOtherAgent.value
);

const showSelfAssignBanner = computed(() => {
  return requiresAssumptionBeforeReply.value;
});

const selfAssignBannerMessage = computed(() => {
  if (
    isUnassigned.value &&
    isEligibleUnassignedQueueConversation.value &&
    isUnassignedQueueContext.value &&
    canManageUnassigned.value &&
    !isLumeraiHandoffAwaitingAssumption.value
  ) {
    return t('CONVERSATION.REPLYBOX.NOT_ASSUMED');
  }

  if (!canManuallyAssume.value) {
    if (isUnassigned.value) {
      return t('CONVERSATION.REPLYBOX.AWAITING_AUTO_ASSIGNMENT');
    }

    const assignedAgentName =
      assignedAgent.value?.name || t('CONVERSATION.REPLYBOX.ANOTHER_AGENT');
    return t('CONVERSATION.REPLYBOX.ASSIGNED_WAITING', {
      name: assignedAgentName,
    });
  }

  if (isLumeraiHandoffAwaitingAssumption.value) {
    return t('CONVERSATION.REPLYBOX.LUMERAI_TRANSFERRED');
  }

  if (isUnassigned.value) {
    return t('CONVERSATION.REPLYBOX.NOT_ASSUMED');
  }

  const agentName =
    assignedAgent.value?.name || t('CONVERSATION.REPLYBOX.ANOTHER_AGENT');
  return t('CONVERSATION.REPLYBOX.ASSIGNED_ASSUME', { name: agentName });
});

const showBotHandoffBanner = computed(
  () =>
    isUserTyping.value &&
    currentChat.value?.status === lumeratecConstants.STATUS_TYPE.PENDING
);

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const canAssumeCurrentConversation = computed(
  () =>
    canManuallyAssume.value ||
    (isUnassigned.value &&
      isEligibleUnassignedQueueConversation.value &&
      isUnassignedQueueContext.value &&
      canManageUnassigned.value &&
      !isLumeraiHandoffAwaitingAssumption.value)
);

const botHandoffBannerMessage = computed(() => {
  if (!canManuallyAssume.value && needsAssignmentToCurrentUser.value) {
    return t('CONVERSATION.REPLYBOX.HANDOFF_WAITING_AUTO_ASSIGNMENT');
  }

  return t('CONVERSATION.BOT_HANDOFF_MESSAGE');
});

const botHandoffActionLabel = computed(() => {
  return assignedAgent.value?.id === currentUser.value?.id
    ? t('CONVERSATION.BOT_HANDOFF_REOPEN_ACTION')
    : t('CONVERSATION.BOT_HANDOFF_ACTION');
});

const selfAssignConversation = async () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: lumeratecConstants.STATUS_TYPE.OPEN,
  });
};

const onClickBotHandoff = async () => {
  try {
    await reopenConversation();

    if (needsAssignmentToCurrentUser.value) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};
</script>

<template>
  <Banner
    v-if="showSelfAssignBanner && !showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="selfAssignBannerMessage"
    :has-action-button="canAssumeCurrentConversation"
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <Banner
    v-if="showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="botHandoffBannerMessage"
    :has-action-button="canManuallyAssume"
    :action-button-label="botHandoffActionLabel"
    @primary-action="onClickBotHandoff"
  />
</template>
